import type {
  ProductionHistoryMonthlyItemResponse,
  ProductionHistoryYearlyItemResponse,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import {
  getAverageMonthlyProduction,
  getDailyRunRate,
  getRemainingDaysInMonth,
  isFinalWeekOfYear,
} from './production-history-annual-projection.util';
import { ProductionHorizon, ProductionProjectionModel } from './production-history-dashboard.models';

export function hasProductionHistoryProjectionContract(
  items: Array<{ isProjected?: boolean; premiumSource?: string; actualPremium?: number }>,
): boolean {
  // Updated production-history API always sends these fields, including on actual rows.
  return items.some(
    item => item.isProjected !== undefined || item.premiumSource !== undefined || item.actualPremium !== undefined,
  );
}

export type HistoryHorizonContext = {
  currentYear: number;
  horizon: ProductionHorizon;
  model: ProductionProjectionModel;
  /** The last month the paid data is trusted through, or 0 when that point is in an earlier year. */
  trustedAsOfMonth: number;
  /** The same point as a date, so run rate can annualise on days rather than whole months. */
  trustedAsOfDate: Date | null;
  /**
   * The last transaction actually on file, with NO reporting lag applied (TASKID-3398).
   *
   * Run rate and the AVERAGE row both divide by elapsed days, so they have to count days up to
   * real data. The lagged date belongs to the seasonal methods, which deliberately stand back
   * from the newest weeks because carriers report late.
   */
  paidThroughDate: Date | null;
};

const MONTHS_IN_YEAR = 12;
const MONTHS_IN_QUARTER = 3;

export function getLatestPaidMonth(monthlyItems: ProductionHistoryMonthlyItemResponse[], year: number): number {
  return monthlyItems.reduce((latestMonth, item) => {
    if (item.year !== year) {
      return latestMonth;
    }

    return item.month > latestMonth ? item.month : latestMonth;
  }, 0);
}

export function getYearActualPremium(monthlyItems: ProductionHistoryMonthlyItemResponse[], year: number): number {
  return monthlyItems.reduce((sum, item) => {
    if (item.year !== year) {
      return sum;
    }

    return sum + item.premium;
  }, 0);
}

export function getCurrentYearMonthSeries(
  monthlyItems: ProductionHistoryMonthlyItemResponse[],
  yearlyItems: ProductionHistoryYearlyItemResponse[],
  context: HistoryHorizonContext,
): Array<number | null> {
  const series: Array<number | null> = Array.from({ length: MONTHS_IN_YEAR }, () => null);

  monthlyItems.forEach(item => {
    if (item.year !== context.currentYear) {
      return;
    }

    series[item.month - 1] = item.premium;
  });

  if (context.horizon !== ProductionHorizon.Projection) {
    return series;
  }

  /**
   * Run rate takes no shape from history at all (TASKID-3398): each month is what it has already
   * booked plus the daily rate over the days it has left, so a 30-day month comes back shorter
   * than a 31-day one and December stops inheriting a heavy Q4 share.
   */
  if (context.model === ProductionProjectionModel.RunRate) {
    return applyRunRateProjection(series, context);
  }

  const dataLatestPaidMonth = getLatestPaidMonth(monthlyItems, context.currentYear);
  const latestPaidMonth =
    context.trustedAsOfMonth > 0 ? Math.min(dataLatestPaidMonth, context.trustedAsOfMonth) : dataLatestPaidMonth;
  if (!latestPaidMonth || latestPaidMonth >= MONTHS_IN_YEAR) {
    return series;
  }

  const projectedRemaining = getProjectedRemainingMonthValues(monthlyItems, yearlyItems, context, latestPaidMonth);
  projectedRemaining.forEach((value, month) => {
    if (value === null) {
      return;
    }

    series[month - 1] = value;
  });

  return series;
}

export function getCurrentYearQuarterValue(
  monthSeries: Array<number | null>,
  quarter: number,
  horizon: ProductionHorizon,
): number | null {
  const startMonthIndex = (quarter - 1) * MONTHS_IN_QUARTER;
  const quarterValues = monthSeries.slice(startMonthIndex, startMonthIndex + MONTHS_IN_QUARTER);
  const presentValues = quarterValues.filter((value): value is number => value !== null);

  if (!presentValues.length) {
    return null;
  }

  if (horizon === ProductionHorizon.Actual) {
    return presentValues.reduce((sum, value) => sum + value, 0);
  }

  return quarterValues.reduce((sum, value) => sum + (value ?? 0), 0);
}

export function getCurrentYearAnnualValue(monthSeries: Array<number | null>): number | null {
  const presentValues = monthSeries.filter((value): value is number => value !== null);
  if (!presentValues.length) {
    return null;
  }

  return presentValues.reduce((sum, value) => sum + value, 0);
}

/**
 * The summary row's label (TASKID-3398).
 *
 * It used to name the projection model, which said nothing: a row reading "RUN RATE" under a
 * run-rate projection is not a second opinion on anything. The row now belongs to Actual
 * Production, where it does mean something — the average month the year has actually produced.
 */
export const PRODUCTION_HISTORY_AVERAGE_LABEL = 'AVERAGE';

/**
 * The AVERAGE row's figure for the current year: actual production per average month, measured
 * over elapsed DAYS so a part-finished month does not drag it down.
 *
 * Prior years are complete, so their stored average already equals this and is used as it stands.
 */
export function getAverageMonthlyProductionRate(
  monthlyItems: ProductionHistoryMonthlyItemResponse[],
  context: HistoryHorizonContext,
): number | null {
  if (!getLatestPaidMonth(monthlyItems, context.currentYear)) {
    return null;
  }

  const ytd = getYearActualPremium(monthlyItems, context.currentYear);
  const asOfDate = context.paidThroughDate;
  if (!asOfDate || asOfDate.getFullYear() !== context.currentYear) {
    return getMonthlyRunRate(monthlyItems, context.currentYear);
  }

  return getAverageMonthlyProduction(ytd, asOfDate) ?? getMonthlyRunRate(monthlyItems, context.currentYear);
}

/** Every month gets its own actual plus the daily run rate over the days it has left. */
function applyRunRateProjection(series: Array<number | null>, context: HistoryHorizonContext): Array<number | null> {
  const asOfDate = context.paidThroughDate;
  if (!asOfDate || asOfDate.getFullYear() !== context.currentYear) {
    return series;
  }

  const ytd = series.reduce<number>((sum, value) => sum + (value ?? 0), 0);
  const dailyRunRate = ytd > 0 ? getDailyRunRate(ytd, asOfDate) : null;
  if (dailyRunRate === null) {
    return series;
  }

  return series.map((value, index) => {
    const remainingDays = getRemainingDaysInMonth(context.currentYear, index + 1, asOfDate);

    // No days left means the month is settled; its actual stands, projection mode or not.
    return remainingDays > 0 ? (value ?? 0) + dailyRunRate * remainingDays : value;
  });
}

function getProjectedRemainingMonthValues(
  monthlyItems: ProductionHistoryMonthlyItemResponse[],
  yearlyItems: ProductionHistoryYearlyItemResponse[],
  context: HistoryHorizonContext,
  latestPaidMonth: number,
): Array<number | null> {
  const remainingMonths = Array.from(
    { length: MONTHS_IN_YEAR - latestPaidMonth },
    (_, index) => latestPaidMonth + 1 + index,
  );
  const values: Array<number | null> = Array.from({ length: MONTHS_IN_YEAR + 1 }, () => null);
  const remainingTotal = getRemainingProjectedPremium(monthlyItems, yearlyItems, context, latestPaidMonth);

  if (remainingTotal === null || !remainingMonths.length) {
    return values;
  }

  // Run rate never reaches here — it is day-based and short-circuits above.
  const weights = getRemainingMonthWeights(monthlyItems, context.currentYear, remainingMonths);

  remainingMonths.forEach((month, index) => {
    values[month] = remainingTotal * weights[index];
  });

  return values;
}

function getRemainingProjectedPremium(
  monthlyItems: ProductionHistoryMonthlyItemResponse[],
  yearlyItems: ProductionHistoryYearlyItemResponse[],
  context: HistoryHorizonContext,
  latestPaidMonth: number,
): number | null {
  const ytd = getYearActualPremium(monthlyItems, context.currentYear);
  const projectedAnnual = getProjectedAnnualPremium(monthlyItems, yearlyItems, context, latestPaidMonth, ytd);
  if (projectedAnnual === null) {
    return null;
  }

  return Math.max(projectedAnnual - ytd, 0);
}

function getProjectedAnnualPremium(
  monthlyItems: ProductionHistoryMonthlyItemResponse[],
  yearlyItems: ProductionHistoryYearlyItemResponse[],
  context: HistoryHorizonContext,
  latestPaidMonth: number,
  ytd: number,
): number | null {
  if (!latestPaidMonth) {
    return null;
  }

  if (latestPaidMonth >= MONTHS_IN_YEAR) {
    return ytd;
  }

  // Inside the last week of the year the figure on file is the year's total, not something to project.
  if (context.trustedAsOfDate && isFinalWeekOfYear(context.trustedAsOfDate)) {
    return ytd;
  }

  const completionFraction = getCompletionFraction(monthlyItems, yearlyItems, context.currentYear, latestPaidMonth);
  if (completionFraction <= 0) {
    return ytd;
  }

  const pace = ytd / completionFraction;
  if (context.model === ProductionProjectionModel.Pace) {
    return pace;
  }

  const prior = getPriorYearPremium(monthlyItems, yearlyItems, context.currentYear);
  if (prior === null) {
    return pace;
  }

  return completionFraction * pace + (1 - completionFraction) * prior;
}

function getMonthlyRunRate(monthlyItems: ProductionHistoryMonthlyItemResponse[], year: number): number {
  const yearItems = monthlyItems.filter(item => item.year === year);
  const latestItem = yearItems.reduce<ProductionHistoryMonthlyItemResponse | null>((latest, item) => {
    if (!latest || item.month > latest.month) {
      return item;
    }

    return latest;
  }, null);
  const actualMonthsCount = new Set(yearItems.map(item => item.month)).size;
  const ytd = getYearActualPremium(monthlyItems, year);

  return latestItem?.avgPerYear ?? (actualMonthsCount ? ytd / actualMonthsCount : 0);
}

function getCompletionFraction(
  monthlyItems: ProductionHistoryMonthlyItemResponse[],
  yearlyItems: ProductionHistoryYearlyItemResponse[],
  currentYear: number,
  asOfMonth: number,
): number {
  const fractions = getHistoricalCompletionFractions(monthlyItems, yearlyItems, currentYear, asOfMonth);
  if (!fractions.length) {
    return asOfMonth / MONTHS_IN_YEAR;
  }

  return fractions.reduce((sum, value) => sum + value, 0) / fractions.length;
}

function getHistoricalCompletionFractions(
  monthlyItems: ProductionHistoryMonthlyItemResponse[],
  yearlyItems: ProductionHistoryYearlyItemResponse[],
  currentYear: number,
  asOfMonth: number,
): number[] {
  const years = new Set([...monthlyItems.map(item => item.year), ...yearlyItems.map(item => item.year)]);

  return Array.from(years)
    .filter(year => year < currentYear)
    .map(year => {
      const ytd = monthlyItems
        .filter(item => item.year === year && item.month <= asOfMonth)
        .reduce((sum, item) => sum + item.premium, 0);
      const fullYear = getFullYearPremium(monthlyItems, yearlyItems, year);
      if (fullYear === null || fullYear <= 0 || ytd <= 0) {
        return null;
      }

      return ytd / fullYear;
    })
    .filter((value): value is number => value !== null);
}

function getFullYearPremium(
  monthlyItems: ProductionHistoryMonthlyItemResponse[],
  yearlyItems: ProductionHistoryYearlyItemResponse[],
  year: number,
): number | null {
  const yearlyItem = yearlyItems.find(item => item.year === year);
  if (yearlyItem) {
    return yearlyItem.premium;
  }

  const yearMonths = monthlyItems.filter(item => item.year === year);
  if (yearMonths.length < MONTHS_IN_YEAR) {
    return null;
  }

  return yearMonths.reduce((sum, item) => sum + item.premium, 0);
}

function getPriorYearPremium(
  monthlyItems: ProductionHistoryMonthlyItemResponse[],
  yearlyItems: ProductionHistoryYearlyItemResponse[],
  currentYear: number,
): number | null {
  return getFullYearPremium(monthlyItems, yearlyItems, currentYear - 1);
}

function getRemainingMonthWeights(
  monthlyItems: ProductionHistoryMonthlyItemResponse[],
  currentYear: number,
  remainingMonths: number[],
): number[] {
  const equalWeight = 1 / remainingMonths.length;
  const historicalShares = remainingMonths.map(month => getHistoricalMonthShare(monthlyItems, currentYear, month));
  const shareTotal = historicalShares.reduce((sum, value) => sum + value, 0);

  if (shareTotal <= 0) {
    return remainingMonths.map(() => equalWeight);
  }

  return historicalShares.map(share => share / shareTotal);
}

function getHistoricalMonthShare(
  monthlyItems: ProductionHistoryMonthlyItemResponse[],
  currentYear: number,
  month: number,
): number {
  const monthItems = monthlyItems.filter(item => item.year < currentYear && item.month === month);
  if (!monthItems.length) {
    return 0;
  }

  return monthItems.reduce((sum, item) => sum + item.premium, 0) / monthItems.length;
}
